import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Lock, Mail, User, Phone, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5b06bfd4-5798-4290-968c-c27d6c6d2c81/artifacts/krjgv0n0_LOGO01.png";

export default function ClientAuth() {
  const navigate = useNavigate();
  const { clientLogin, clientRegister, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });

  if (user && user.role === 'client') {
    navigate('/my-bookings', { replace: true });
    return null;
  }
  if (user && user.role === 'admin') {
    navigate('/admin', { replace: true });
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await clientLogin(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate('/my-bookings', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid email or password');
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (registerForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await clientRegister(registerForm.name, registerForm.email, registerForm.phone, registerForm.password);
      toast.success('Account created! Welcome to Healing Hands.');
      navigate('/my-bookings', { replace: true });
    } catch (err) {
      setError(err.message || 'Registration failed');
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-healing-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Healing Hands" className="h-20 w-auto mx-auto mb-4 cursor-pointer" onClick={() => navigate('/')} />
          <h1 className="font-heading text-2xl text-healing-burgundy">
            {isLogin ? 'Client Login' : 'Create Account'}
          </h1>
          <p className="font-body text-healing-burgundy-light">
            {isLogin ? 'Sign in to view your bookings' : 'Join Healing Hands to manage your appointments'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-healing-border">
          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-5" data-testid="client-login-form">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-healing-burgundy font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-healing-burgundy-light" />
                  <Input
                    id="login-email"
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="pl-10 border-healing-border focus:ring-healing-purple"
                    required
                    data-testid="client-login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-healing-burgundy font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-healing-burgundy-light" />
                  <Input
                    id="login-password"
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Enter your password"
                    className="pl-10 border-healing-border focus:ring-healing-purple"
                    required
                    data-testid="client-login-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-healing-purple hover:bg-healing-purple-hover text-white py-6 rounded-xl"
                data-testid="client-login-submit"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Signing in...
                  </>
                ) : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="client-register-form">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="reg-name" className="text-healing-burgundy font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-healing-burgundy-light" />
                  <Input
                    id="reg-name"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Your full name"
                    className="pl-10 border-healing-border focus:ring-healing-purple"
                    required
                    data-testid="client-register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-email" className="text-healing-burgundy font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-healing-burgundy-light" />
                  <Input
                    id="reg-email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="pl-10 border-healing-border focus:ring-healing-purple"
                    required
                    data-testid="client-register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-phone" className="text-healing-burgundy font-medium">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-healing-burgundy-light" />
                  <Input
                    id="reg-phone"
                    type="tel"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="pl-10 border-healing-border focus:ring-healing-purple"
                    required
                    data-testid="client-register-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-password" className="text-healing-burgundy font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-healing-burgundy-light" />
                  <Input
                    id="reg-password"
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Min. 6 characters"
                    className="pl-10 border-healing-border focus:ring-healing-purple"
                    required
                    data-testid="client-register-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-confirm" className="text-healing-burgundy font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-healing-burgundy-light" />
                  <Input
                    id="reg-confirm"
                    type="password"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm(p => ({ ...p, confirmPassword: e.target.value }))}
                    placeholder="Confirm your password"
                    className="pl-10 border-healing-border focus:ring-healing-purple"
                    required
                    data-testid="client-register-confirm"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-healing-purple hover:bg-healing-purple-hover text-white py-6 rounded-xl"
                data-testid="client-register-submit"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating Account...
                  </>
                ) : 'Create Account'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-healing-purple hover:text-healing-purple-hover transition-colors font-body text-sm"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-healing-burgundy-light hover:text-healing-purple transition-colors font-body text-sm inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to website
          </button>
        </div>
      </div>
    </div>
  );
}
